import type {
  IReservation,
  IReservationCustomerSummary,
} from '@/interfaces/IReservation';
import {
  addDays,
  defaultBoardDate,
  IReservationInstant,
} from './reservation-time';

/** Mirrors RESERVATION_NO_SHOW_REVIEW_DAYS in the API status machine. */
export const RESERVATION_NO_SHOW_REVIEW_DAYS = 7;

export type IStaffActionId =
  | 'confirm'
  | 'arrive'
  | 'seat'
  | 'noShow'
  | 'confirmNoShow'
  | 'cancel'
  | 'finish'
  | 'undoSeat'
  | 'reopen'
  | 'attended'
  | 'openTable'
  | 'waiveDeposit'
  | 'resendPayment'
  | 'retryRefund'
  | 'refundRemainder'
  | 'keepRemainder';
export interface IStaffAction {
  id: IStaffActionId;
  labelKey: string;
  primary: boolean;
  destructive: boolean;
  toStatus?: IReservation['status'];
  requiresOrdersManage?: boolean;
}
type IActionReservation = Pick<
  IReservation,
  | 'status'
  | 'serviceDate'
  | 'seatedAt'
  | 'depositStatus'
  | 'depositUnappliedAmount'
  | 'autoClosed'
>;

const action = (
  id: IStaffActionId,
  toStatus?: IReservation['status'],
  primary = false,
  destructive = false
): IStaffAction => ({
  id,
  toStatus,
  primary,
  destructive,
  labelKey: `reservations.action.${id}`,
});

/** A failed deposit refund can be retried whatever the booking's status. */
const retryRefund = (reservation: IActionReservation): IStaffAction[] =>
  reservation.depositStatus === 'refund_failed'
    ? [
        {
          ...action('retryRefund'),
          labelKey: 'reservations.deposit.retryRefund',
        },
      ]
    : [];

export function getStaffActions(
  reservation: IActionReservation,
  now: IReservationInstant,
  timeZone: string,
  businessDayStartHour = 4
): IStaffAction[] {
  switch (reservation.status) {
    case 'pending':
      return [
        action('confirm', 'confirmed', true),
        action('cancel', 'cancelled', false, true),
        ...retryRefund(reservation),
      ];
    case 'pending_payment':
      return [
        {
          ...action('waiveDeposit', undefined, true),
          labelKey: 'reservations.deposit.waive',
        },
        {
          ...action('resendPayment'),
          labelKey: 'reservations.detail.resendPayment',
        },
        action('cancel', 'cancelled', false, true),
      ];
    case 'confirmed':
      return [
        action('arrive', 'arrived', true),
        action('seat', 'seated'),
        action('noShow', 'no_show'),
        action('cancel', 'cancelled', false, true),
        ...retryRefund(reservation),
      ];
    case 'arrived':
      return [
        action('seat', 'seated', true),
        action('noShow', 'no_show'),
        action('cancel', 'cancelled', false, true),
        ...retryRefund(reservation),
      ];
    case 'seated': {
      const result = [action('finish', 'finished', true)];
      const elapsed =
        new Date(now).getTime() -
        new Date(reservation.seatedAt ?? '').getTime();
      if (elapsed >= 0 && elapsed <= 5 * 60000)
        result.push(action('undoSeat', 'arrived'));
      result.push({
        ...action('openTable'),
        labelKey: 'reservations.detail.openTable',
        requiresOrdersManage: true,
      });
      result.push(...retryRefund(reservation));
      return result;
    }
    case 'no_show': {
      const today = defaultBoardDate(now, timeZone, businessDayStartHour);
      const sameDay = reservation.serviceDate === today;
      const reviewable =
        addDays(reservation.serviceDate, RESERVATION_NO_SHOW_REVIEW_DAYS) >=
        today;
      return [
        // The guest is here now: reopen the visit instead of closing it.
        ...(sameDay ? [action('reopen', 'arrived', true)] : []),
        // A past visit the host forgot to mark: record it as attended.
        ...(!sameDay && reviewable
          ? [
              {
                ...action('attended', 'finished', true),
                labelKey: 'reservations.action.reopen',
              },
            ]
          : []),
        // Only an automatic close-out still needs the absence confirmed.
        ...(reservation.autoClosed
          ? [
              {
                ...action('confirmNoShow', undefined, false, true),
                labelKey: 'reservations.action.noShow',
              },
            ]
          : []),
        ...retryRefund(reservation),
      ];
    }
    case 'finished':
    case 'cancelled': {
      const result = retryRefund(reservation);
      if ((reservation.depositUnappliedAmount ?? 0) > 0)
        result.push(
          {
            ...action('refundRemainder'),
            labelKey: 'reservations.deposit.refundLeftover',
          },
          {
            ...action('keepRemainder'),
            labelKey: 'reservations.deposit.keepLeftover',
          }
        );
      return result;
    }
  }
}

export function getPrimaryStaffAction(
  reservation: IActionReservation,
  now: IReservationInstant,
  timeZone: string,
  businessDayStartHour = 4
): IStaffAction | null {
  return (
    getStaffActions(reservation, now, timeZone, businessDayStartHour).find(
      (value) => value.primary
    ) ?? null
  );
}

export function isLate(
  reservation: Pick<IReservation, 'status' | 'startsAt'>,
  now: IReservationInstant,
  toleranceMin: number
): boolean {
  return (
    (reservation.status === 'confirmed' || reservation.status === 'arrived') &&
    new Date(now).getTime() >
      new Date(reservation.startsAt).getTime() + toleranceMin * 60000
  );
}

export function lateMinutes(
  reservation: Pick<IReservation, 'status' | 'startsAt'>,
  now: IReservationInstant,
  toleranceMin: number
): number {
  return isLate(reservation, now, toleranceMin)
    ? Math.floor(
        (new Date(now).getTime() - new Date(reservation.startsAt).getTime()) /
          60000
      )
    : 0;
}

export function isNoShowRisk(
  customer: Pick<IReservationCustomerSummary, 'noShowsCount'> | null | undefined
): boolean {
  return (customer?.noShowsCount ?? 0) >= 2;
}
